"use client";

import React, { createContext, useContext, useCallback, useReducer, useRef } from "react";
import { io, Socket } from "socket.io-client";

// ─── Types ─────────────────────────────────────────────────────────────────

export type ImportPhase =
	| "CLEANUP"
	| "CONTACTS"
	| "CHAT_NAMES"
	| "GROUPS"
	| "MESSAGES"
	| "FINALIZING";

export type ImportStatus = "idle" | "importing" | "success" | "error";

export interface ImportResult {
	contacts: number;
	chats: number;
	messages: number;
	groups: number;
	duration: string;
}

export interface ImportState {
	status: ImportStatus;
	jobId: string | null;
	sessionId: string | null;
	phase: ImportPhase | null;
	current: number;
	total: number;
	message: string;
	result: ImportResult | null;
	error: string | null;
	startTime: number;
	elapsed: number;
}

type ImportAction =
	| { type: "START"; jobId: string; sessionId: string }
	| { type: "PROGRESS"; phase: ImportPhase; current: number; total: number; message: string }
	| { type: "COMPLETE"; result: ImportResult }
	| { type: "ERROR"; error: string }
	| { type: "DISMISS" };

const PHASE_LABELS: Record<ImportPhase, string> = {
	CLEANUP: "Temizleniyor",
	CONTACTS: "Kişiler alınıyor",
	CHAT_NAMES: "İsimler çözümleniyor",
	GROUPS: "Gruplar alınıyor",
	MESSAGES: "Mesajlar alınıyor",
	FINALIZING: "Sonlandırılıyor",
};

function importReducer(state: ImportState, action: ImportAction): ImportState {
	switch (action.type) {
		case "START":
			return {
				status: "importing",
				jobId: action.jobId,
				sessionId: action.sessionId,
				phase: "CLEANUP",
				current: 0,
				total: 0,
				message: "Başlıyor...",
				result: null,
				error: null,
				startTime: Date.now(),
				elapsed: 0,
			};
		case "PROGRESS":
			return {
				...state,
				phase: action.phase,
				current: action.current,
				total: action.total,
				message: action.message,
			};
		case "COMPLETE":
			return {
				...state,
				status: "success",
				phase: "FINALIZING",
				current: 1,
				total: 1,
				message: "Tamamlandı",
				result: action.result,
				elapsed: parseFloat(action.result.duration) || (Date.now() - state.startTime) / 1000,
			};
		case "ERROR":
			return {
				...state,
				status: "error",
				error: action.error,
				elapsed: (Date.now() - state.startTime) / 1000,
			};
		case "DISMISS":
			return {
				status: "idle",
				jobId: null,
				sessionId: null,
				phase: null,
				current: 0,
				total: 0,
				message: "",
				result: null,
				error: null,
				startTime: 0,
				elapsed: 0,
			};
	}
}

const initialState: ImportState = {
	status: "idle",
	jobId: null,
	sessionId: null,
	phase: null,
	current: 0,
	total: 0,
	message: "",
	result: null,
	error: null,
	startTime: 0,
	elapsed: 0,
};

// ─── Context ────────────────────────────────────────────────────────────────

interface ImportContextValue {
	state: ImportState;
	startImport: (sessionId: string, options?: ImportOptions) => Promise<string | null>;
	dismissImport: () => void;
}

export interface ImportOptions {
	contacts?: boolean;
	chats?: boolean;
	groups?: boolean;
	messageLimit?: number;
}

const ImportContext = createContext<ImportContextValue | null>(null);

export function useImport() {
	const ctx = useContext(ImportContext);
	if (!ctx) throw new Error("useImport must be used within <ImportProvider>");
	return ctx;
}

export { PHASE_LABELS };

// ─── Provider ───────────────────────────────────────────────────────────────

export function ImportProvider({ children }: { children: React.ReactNode }) {
	const [state, dispatch] = useReducer(importReducer, initialState);
	const importSocketRef = useRef<Socket | null>(null);

	const startImport = useCallback(async (
		sessionId: string,
		options: ImportOptions = {},
	): Promise<string | null> => {
		// Önceki import socket'ini temizle
		if (importSocketRef.current) {
			importSocketRef.current.disconnect();
			importSocketRef.current = null;
		}

		// Import için Socket.IO dinle
		const importSocket = io({
			path: "/api/socket/io",
			addTrailingSlash: false,
		});
		importSocketRef.current = importSocket;

		importSocket.on("connect", () => {
			importSocket.emit("join-session", sessionId);
		});

		importSocket.on("import.progress", (data: {
			jobId: string;
			phase: ImportPhase;
			current: number;
			total: number;
			message: string;
		}) => {
			dispatch({
				type: "PROGRESS",
				phase: data.phase,
				current: data.current,
				total: data.total,
				message: data.message,
			});
		});

		importSocket.on("import.complete", (data: {
			jobId: string;
			result: ImportResult;
		}) => {
			dispatch({ type: "COMPLETE", result: data.result });
		});

		importSocket.on("import.error", (data: {
			jobId: string;
			error: string;
		}) => {
			dispatch({ type: "ERROR", error: data.error });
		});

		try {
			const res = await fetch(`/api/sessions/${sessionId}/import`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					contacts: options.contacts !== false,
					chats: options.chats !== false,
					groups: options.groups !== false,
					messageLimit: options.messageLimit || 20,
				}),
			});

			if (!res.ok) {
				const errBody = await res.json().catch(() => ({}));
				// 409 = zaten aktif import var
				if (res.status === 409 && errBody.jobId) {
					dispatch({ type: "START", jobId: errBody.jobId, sessionId });
					return errBody.jobId;
				}
				throw new Error(errBody.message || `HTTP ${res.status}`);
			}

			const data = await res.json();
			dispatch({ type: "START", jobId: data.jobId, sessionId });
			return data.jobId;
		} catch (err: unknown) {
			dispatch({
				type: "ERROR",
				error: err instanceof Error ? err.message : "Import sırasında hata oluştu",
			});
			return null;
		}
		// Socket'i bağlantıda bırak — import tamamlanana kadar progress dinlemeli
	}, []);

	const dismissImport = useCallback(() => {
		if (importSocketRef.current) {
			importSocketRef.current.disconnect();
			importSocketRef.current = null;
		}
		dispatch({ type: "DISMISS" });
	}, []);

	return (
		<ImportContext.Provider value={{ state, startImport, dismissImport }}>
			{children}
		</ImportContext.Provider>
	);
}
